import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type Paged, type ProjectRow } from '../api';
import { useMe } from '../auth';
import { cacheGet, cacheSet } from '../offline';

export const OTHER_CONSULTANT_WARNING = 'Este projeto está vinculado a outro consultor. Deseja continuar?';

// Lista de projetos ativos: vinculados ao consultor primeiro. Fica em cache no aparelho para o rascunho sem conexão.
export function useProjectOptions() {
  const me = useMe();
  const [cached, setCached] = useState<ProjectRow[] | null>(null);
  const q = useQuery({
    queryKey: ['projects', 'picker'],
    queryFn: async () => {
      const res = await api.get<Paged<ProjectRow>>('/projects?scope=all&status=ativo&pageSize=200');
      void cacheSet(me.id, 'projects-picker', res.data).catch(() => undefined);
      return res.data;
    },
    staleTime: 60_000,
  });
  useEffect(() => {
    if (q.isError) void cacheGet<ProjectRow[]>(me.id, 'projects-picker').then((c) => setCached(c?.value ?? [])).catch(() => setCached([]));
  }, [q.isError, me.id]);
  return { projects: q.data ?? cached ?? [], loading: q.isLoading, fromCache: !q.data && cached !== null };
}

interface Props {
  id?: string;
  value: string | null;
  onChange: (projectId: string | null, project: ProjectRow | null) => void;
  allowEmpty?: string; // rótulo da opção vazia
  disabled?: boolean;
  describedBy?: string;
  invalid?: boolean;
}

// Seletor de projeto (seção 3.2): vinculados primeiro; opção explícita para pesquisar todos;
// projeto de outro consultor pede só uma confirmação simples, sem justificativa.
export function ProjectPicker({ id, value, onChange, allowEmpty, disabled, describedBy, invalid }: Props) {
  const { projects, loading, fromCache } = useProjectOptions();
  const [showAll, setShowAll] = useState(false);
  const linked = projects.filter((p) => p.linkedToMe);
  const others = projects.filter((p) => !p.linkedToMe);
  const selected = projects.find((p) => p.id === value);
  const showOthers = showAll || (selected && !selected.linkedToMe);
  const label = (p: ProjectRow) => `${p.name} — ${p.clientTradeName || p.clientName}`;

  function choose(pid: string) {
    if (!pid) return onChange(null, null);
    const p = projects.find((x) => x.id === pid) ?? null;
    if (p && !p.linkedToMe && !window.confirm(OTHER_CONSULTANT_WARNING)) return;
    onChange(pid, p);
  }

  return (
    <div style={{ display: 'grid', gap: '0.35rem' }}>
      <select id={id} value={value ?? ''} disabled={disabled || loading} onChange={(e) => choose(e.target.value)} aria-describedby={describedBy} aria-invalid={invalid || undefined}>
        <option value="">{loading ? 'Carregando…' : (allowEmpty ?? 'Selecione o projeto…')}</option>
        <optgroup label="Meus projetos">
          {linked.map((p) => (
            <option key={p.id} value={p.id}>
              {label(p)}
            </option>
          ))}
        </optgroup>
        {showOthers && (
          <optgroup label="Projetos de outros consultores">
            {others.map((p) => (
              <option key={p.id} value={p.id}>
                {label(p)} (resp.: {p.ownerName})
              </option>
            ))}
          </optgroup>
        )}
      </select>
      <label className="check small" style={{ minHeight: 32 }}>
        <input type="checkbox" checked={Boolean(showOthers)} disabled={Boolean(selected && !selected.linkedToMe)} onChange={(e) => setShowAll(e.target.checked)} />
        Pesquisar todos os projetos da TECHNOVE
      </label>
      {fromCache && <span className="hint">Sem conexão: lista de projetos salva neste aparelho.</span>}
      {selected && !selected.linkedToMe && <span className="hint">Projeto de {selected.ownerName}. Registro permitido sem justificativa.</span>}
    </div>
  );
}
